import { ReactNode, createContext, useContext, useMemo } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Packaging, useGetOnePackaging } from "src/api/client-api/packagings/useGetOnePackaging";
import { useGetPackagingPost } from "src/api/client-api/post/useGetPackagingPost";
import { Post } from "src/api/client-api/post/postSchema";
import { SsqVersion, useGetOneSsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";
import { Loading } from "src/components/utils/Loading.tsx";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { ScoreService } from "src/services/ScoreService";
import { EntityForbiddenForCampaignPage } from "src/components/PageState/EntityForbiddenForCampaignPage";
import { RemoteEntityStateBoundary } from "src/components/PageState/RemoteEntityStateBoundary";
import type { FetchPostForAnswers } from "src/context/ScoreDetail/context";
type PackagingContextInitialValues = {
    packaging: Packaging | undefined;
    isPackagingLoading: boolean;

    ssqVersion: SsqVersion | undefined;
    post: Post | undefined;
    isSsqVersionLoading: boolean;
    isSsqVersionError: boolean;
    ssqVersionError: unknown;
    refetchSsqVersion: () => Promise<unknown>;
    isCampaignError: boolean;
    campaignError: unknown;
    retryCampaigns: () => Promise<unknown>;
    isPostUnavailable: boolean;
    isPostIdle: boolean;
    isPostLoading: boolean;
    isPostSuccess: boolean;
    isPostError: boolean;
    postError: unknown;
    refetchPost: FetchPostForAnswers;
};
const PackagingContext = createContext<PackagingContextInitialValues | null>(null);

type PackagingContextProviderProps = { children: ReactNode };
export const PackagingContextProvider = (props: PackagingContextProviderProps) => {
    const { children } = props;
    const { t } = useTranslation();
    const { packagingId } = useParams();
    const { selectedCompany, currentCampaign, campaignsError, campaignsStatus, retryCampaigns } = useFoodpilotContext();
    const { lastSelectedVariation } = useEntityScores();

    const scoresQuery = useMemo(
        () => ({
            scores: {
                variations: ScoreService.getVariationByLastSelectedVariation(lastSelectedVariation),
            },
        }),
        [lastSelectedVariation.scoreId, lastSelectedVariation.variationId],
    );

    const {
        packaging,
        packagingLoadState,
        isLoading: isPackagingLoading,
        refetch: refetchPackaging,
    } = useGetOnePackaging({ packagingId, scoresQuery });

    // ProtectedContext only exposes FoodpilotContext after campaign initialization.
    // Once the packaging is loaded, a missing company/campaign is therefore a
    // terminal prerequisite state, not a post query that may eventually start.
    const isCampaignError = campaignsStatus === "error";
    const isPostUnavailable =
        packaging !== undefined &&
        campaignsStatus === "success" &&
        (selectedCompany === null || currentCampaign === null);

    const {
        post,
        error: postError,
        isError: isPostError,
        isIdle: isPostIdle,
        isLoading: isPostLoading,
        isSuccess: isPostSuccess,
        refetch: refetchPost,
    } = useGetPackagingPost({
        packagingId: packaging?.id,
        companyId: selectedCompany?.id,
        campaignId: currentCampaign?.id,
        enabled: packaging !== undefined && campaignsStatus === "success" && !isPostUnavailable,
    });

    const ssqVersionId = post?.ssqVersionId;

    const {
        ssqVersion,
        error: ssqVersionError,
        isError: isSsqVersionError,
        isReady: isSsqVersionReady,
        isLoading: isSsqVersionLoading,
        refetch: refetchSsqVersion,
    } = useGetOneSsqVersion(ssqVersionId);

    const items: PackagingContextInitialValues = {
        packaging,
        isPackagingLoading,
        ssqVersion,
        isSsqVersionLoading,
        isSsqVersionError,
        ssqVersionError,
        refetchSsqVersion,
        isCampaignError,
        campaignError: campaignsError,
        retryCampaigns,
        post,
        isPostUnavailable,
        isPostIdle,
        isPostLoading,
        isPostSuccess,
        isPostError,
        postError,
        refetchPost,
    };
    const packagingErrorContent = {
        "contract-error": t("packaging.contract_error", "Packaging data is incomplete. Editing is disabled."),
        forbidden: t("packaging.error.forbidden", "You are forbidden from viewing this packaging."),
        "not-found": t("packaging.error.not_found", "This packaging was not found."),
        "server-error": t("packaging.error.server", "The packaging could not be loaded. Please try again."),
    };

    return (
        <RemoteEntityStateBoundary
            errorContent={packagingErrorContent}
            forbiddenContent={<EntityForbiddenForCampaignPage />}
            onRetry={() => refetchPackaging()}
            state={packagingLoadState}
        >
            {isSsqVersionReady === false ?
                <Loading />
            :   <PackagingContext.Provider value={items}>{children}</PackagingContext.Provider>}
        </RemoteEntityStateBoundary>
    );
};

export const usePackagingContext = () => {
    const context = useContext(PackagingContext);

    if (context) {
        return context;
    }

    throw new Error(`usePackagingContext must be used within an PackagingContextProvider`);
};
